import { useCallback, useEffect, useRef, useState } from 'react';
import { addConfirmedScanQuantity, scanGroupKey } from '@/lib/scanItems';

// Each duplicate is a separate, explicit decision. Unconfirmed scans never mutate the list.
export const useRepeatScan = (itemsRef, setItems) => {
  const requests = useRef([]);
  const [request, setRequest] = useState(null);
  useEffect(() => () => { requests.current.splice(0).forEach(r => r.resolve(false)); }, []);
  const askRepeat = useCallback((itemKey, sourceKey = null) => new Promise(resolve => {
    const source = itemsRef.current.find(i => i._key === sourceKey);
    const target = itemsRef.current.find(i => i._key === itemKey);
    if (!target) { resolve(false); return; }
    const entry = { id: crypto.randomUUID(), itemKey, sourceKey, groupKey: scanGroupKey(target), initialQuantity: Number(source?.aantal) || 1, resolve };
    requests.current.push(entry);
    if (requests.current.length === 1) setRequest(entry);
  }), [itemsRef]);
  const finish = useCallback((quantity, unit) => {
    const current = requests.current[0];
    if (!current) return false;
    let added = false;
    if (quantity != null) {
      const original = itemsRef.current.find(i => i._key === current.itemKey);
      if (original && scanGroupKey(original) === current.groupKey) {
        const updated = addConfirmedScanQuantity(original, quantity, unit);
        if (!updated) return false;
        setItems(previous => [updated, ...previous.filter(i => i._key !== current.itemKey && i._key !== current.sourceKey)]);
        added = true;
      }
    }
    requests.current.shift();
    while (requests.current[0] && !itemsRef.current.some(i => i._key === requests.current[0].itemKey && scanGroupKey(i) === requests.current[0].groupKey)) {
      requests.current.shift().resolve(false);
    }
    setRequest(requests.current[0] || null);
    current.resolve(added);
    return true;
  }, [itemsRef, setItems]);
  const currentItems = itemsRef.current;
  useEffect(() => {
    if (request && requests.current[0]?.id === request.id && !currentItems.some(i => i._key === request.itemKey && scanGroupKey(i) === request.groupKey)) {
      finish(null); // A late row replacement/removal must never leave an invisible blocking dialog.
    }
  }, [currentItems, request, finish]);
  const cancelRepeat = useCallback(() => finish(null), [finish]);
  return { repeatRequest: request, askRepeat, confirmRepeat: finish, cancelRepeat };
};